<template>
  <div
    v-if="impersonation"
    role="alert"
    class="flex h-10 w-full shrink-0 items-center justify-center gap-2.5 overflow-hidden bg-amber-500 px-4 text-sm text-[#1f1300] shadow-md"
  >
    <span
      class="whitespace-nowrap rounded-full bg-[#1f1300] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-300"
    >
      Impersonation mode
    </span>
    <span class="truncate">
      <strong class="font-bold">{{ byLabel }}</strong> is viewing as
      <strong class="font-bold">{{ currentLabel }}</strong>
    </span>
    <button
      type="button"
      class="whitespace-nowrap rounded-lg border-2 border-[#1f1300] px-3 py-0.5 text-[13px] font-bold hover:bg-[#1f1300] hover:text-amber-300"
      @click="exitImpersonation"
    >
      Exit impersonation
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/store';
import { exitImpersonation, getImpersonation } from '@/composables/impersonation';

const route = useRoute();
const { user, role } = storeToRefs(useMainStore());

// The app shell (and this component) mounts BEFORE the router's initial
// navigation resolves, and it is the router guard that saves the
// impersonation context from the ?impersonated handoff params. Reading
// localStorage once at setup therefore misses the very first load — the
// banner only appeared after a manual refresh. Re-read the context every
// time a navigation completes (immediate covers the initial one).
const impersonation = ref(getImpersonation());

watch(
  () => route.fullPath,
  () => {
    impersonation.value = getImpersonation();
    document.body.classList.toggle('impersonation-active', !!impersonation.value);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.body.classList.remove('impersonation-active');
});

const byLabel = computed(() => {
  const by = impersonation.value?.by || { name: '', email: '', role: '' };
  let label = by.name || by.email || 'An admin';
  if (by.role) label += ` (${by.role})`;
  return label;
});

const currentLabel = computed(() => {
  let label = user.value?.name || user.value?.email || 'this user';
  const details = [
    user.value?.email && user.value.email !== label ? user.value.email : null,
    role.value?.name || null,
  ]
    .filter(Boolean)
    .join(' · ');
  if (details) label += ` (${details})`;
  return label;
});
</script>

<style>
/* Not scoped on purpose: the content area sizes itself with
   .heightClass { height: calc(100vh - 53px) }. With the 40px (h-10) banner
   docked above the shell, subtract it so the page does not overflow. */
body.impersonation-active .heightClass {
  height: calc(100vh - 53px - 40px);
}
</style>
